// The game home as a status page (spec/dashboard.md section 3.1, the game home; section 12.2, releases and pins; section 12.3,
// merge requests): the Getting started checklist until every step is done, the Releases section, the status rows (open
// merge requests, the unsaved drafts of this tab, the last releases), then Branches, Environments and Recent changes.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ops } from '../api/operations';
import type { MergeRequest, Page } from '../api/types';
import { GettingStarted, readOnboardingHidden, useHomeReleases, useOnboarding, writeOnboardingHidden } from './GettingStarted';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { IconButton } from '../ui/Button';
import { ChangeLog } from '../ui/ChangeLog';
import { Badge } from '../ui/Chip';
import { DataGrid } from '../ui/DataGrid';
import { Check, Copy } from '../ui/icons';
import { formatInstantWithZone, relativeTime, useTimeZone } from '../ui/time';
import { useBranchReleaseStatus, useReleaseStatus } from '../shell/releaseStatus';
import { useQuery } from '../workspace/cache';
import { dirtyBranches } from '../workspace/react';
import { LoadingPane } from '../ui/PageStates';
import './org/org.css';

const kindTone = (kind: string) => (kind === 'prod' ? 'danger' : kind === 'stage' ? 'warning' : 'success');

/**
 * The Releases section of the game home (spec/dashboard.md section 3.3, the release status; section 12.2): per environment
 * the release its default pin serves, the branch, when and by whom it was pinned and why; then how far the default branch's
 * head is ahead of what it runs.
 */
function ReleasesSection() {
  const { org, game, branches, offsets } = useGame();
  const [zone] = useTimeZone();
  const status = useReleaseStatus();
  const defaultBranch = branches.find((b) => b.isDefault) ?? branches[0];
  const main = useBranchReleaseStatus(defaultBranch?.name ?? null);
  const now = Date.now();
  const envLink = (branch: string) => routes.releases(org, game.slug, branch, 'environments');
  return (
    <div style={{ marginBottom: 'var(--hue-space-4)' }}>
      <div className="section-head">
        <h2>Releases</h2>
        <span className="muted">What each environment serves.</span>
      </div>
      {status.loading && <LoadingPane rows={2} label="Loading the releases" />}
      {status.forbidden && <p className="muted">You cannot read the releases of this game.</p>}
      {status.envs && !status.forbidden && (
        <div className="pane" data-testid="game-releases">
          {status.envs.map((e) => (
            <div key={e.env.id} className="release-home-row" data-testid={`game-release-${e.env.slug}`}>
              <Badge tone={kindTone(e.env.kind)}>{e.env.kind}</Badge>
              <span className="env-name">{e.env.name}</span>
              {e.forbidden ? (
                <span className="muted">no access</span>
              ) : e.release ? (
                <>
                  <span className="rh-num">#{e.release.number}</span>
                  <span>
                    from <code>{e.release.branchName}</code>
                  </span>
                  {e.pin && (
                    <span className="muted" title={formatInstantWithZone(e.pin.createdAt, zone, { offsets })}>
                      pinned {relativeTime(e.pin.createdAt, now)}
                      {e.pin.pinnedBy ? ` by ${e.pin.pinnedBy}` : ''}
                    </span>
                  )}
                  {e.pin?.reason && <span className="muted">“{e.pin.reason}”</span>}
                </>
              ) : e.pin ? (
                <span className="muted">pinned release unavailable</span>
              ) : (
                <span className="muted">nothing pinned</span>
              )}
              <Link className="rh-link" to={envLink(e.release?.branchName ?? defaultBranch?.name ?? 'main')}>
                Environments
              </Link>
            </div>
          ))}
          {status.envs.length === 0 && <div className="release-home-row muted">No environments.</div>}
        </div>
      )}
      {defaultBranch &&
        main.environments
          .filter((e) => e.changed && e.changed.documents > 0)
          .map((e) => (
            <div key={e.env.id} className="release-home-ahead" title={`${e.changed!.files} file${e.changed!.files === 1 ? '' : 's'} changed`}>
              <code>{defaultBranch.name}</code> head is {e.changed!.documents} document{e.changed!.documents === 1 ? '' : 's'} ahead of #{e.release.number} on {e.env.slug}
            </div>
          ))}
    </div>
  );
}

/** A merge request's state in one chip: its conflicts, mergeable, or what holds it. */
function mrChip(mr: MergeRequest) {
  const conflicts = Number(mr.unresolved ?? 0) || (mr.conflicts ?? []).length;
  if (conflicts > 0) return <Badge tone="warning">{conflicts} conflict{conflicts === 1 ? '' : 's'}</Badge>;
  if (mr.mergeable) return <Badge tone="success">mergeable</Badge>;
  if (mr.targetProtected && !mr.approved) return <Badge tone="info">needs approval</Badge>;
  return <Badge>not mergeable</Badge>;
}

/**
 * The status rows under Releases (spec/dashboard.md section 3.1): the open merge requests (newest first, up to five), the
 * drafts this tab holds per branch (only when any), and the five newest releases with where each is pinned.
 */
function StatusSection() {
  const { org, game, branches, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const status = useReleaseStatus();
  const releases = useHomeReleases();
  const defaultBranch = branches.find((b) => b.isDefault) ?? branches[0];
  const b = defaultBranch?.name ?? 'main';
  const mrs = useQuery(`game:${org}/${game.slug}:home:merge-requests`, () => client.data(ops.listMergeRequests, { path: { org, game: game.slug }, query: { state: 'open', limit: 20 } }) as unknown as Promise<Page<MergeRequest>>);
  const open = [...(mrs.data?.items ?? [])].sort((x, y) => Number(y.number) - Number(x.number));
  const shown = open.slice(0, 5);
  const dirty = dirtyBranches(game.id).filter((d) => d.count > 0);
  const now = Date.now();
  const pinnedWhere = (releaseId: string) => (status.envs ?? []).filter((e) => e.release?.releaseId === releaseId).map((e) => e.env.slug);
  return (
    <div className="page-section home-status">
      <div className="section-head">
        <h2>Status</h2>
        <span className="muted">What waits for review, what is unsaved, what was built.</span>
      </div>
      <div className="pane">
        <div className="home-status-row" data-testid="home-merge-requests">
          <div className="home-status-label">
            <strong>Merge requests</strong>
            <Link to={routes.mergeRequests(org, game.slug, b)}>{open.length > shown.length ? `All ${open.length} open` : 'All merge requests'}</Link>
          </div>
          <div className="home-status-body">
            {mrs.loading && !mrs.data && <span className="skeleton home-status-skeleton" role="status" aria-label="Loading the merge requests" />}
            {mrs.error !== undefined && !mrs.data && <span className="muted">Could not read the merge requests.</span>}
            {mrs.data && shown.length === 0 && <span className="muted">No open merge request</span>}
            {shown.map((mr) => (
              <div key={mr.id} className="home-status-item" data-testid={`home-mr-${mr.number}`}>
                <span className="rh-num">!{mr.number}</span>
                <Link to={routes.mergeRequest(org, game.slug, b, Number(mr.number))}>{mr.title}</Link>
                <span className="muted">
                  <code>{mr.sourceBranch}</code> → <code>{mr.targetBranch}</code>
                </span>
                {mrChip(mr)}
              </div>
            ))}
          </div>
        </div>
        {dirty.length > 0 && (
          <div className="home-status-row" data-testid="home-unsaved">
            <div className="home-status-label">
              <strong>Unsaved work</strong>
            </div>
            <div className="home-status-body">
              {dirty.map((d) => (
                <div key={d.branch} className="home-status-item">
                  <span>
                    {d.count} unsaved change{d.count === 1 ? '' : 's'} on <code>{d.branch}</code>
                  </span>
                  <Link to={routes.templates(org, game.slug, d.branch)}>Changes</Link>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="home-status-row" data-testid="home-last-releases">
          <div className="home-status-label">
            <strong>Last releases</strong>
            <Link to={routes.releases(org, game.slug, b, 'releases')}>All releases</Link>
          </div>
          <div className="home-status-body">
            {releases.loading && !releases.data && <span className="skeleton home-status-skeleton" role="status" aria-label="Loading the releases" />}
            {releases.error !== undefined && !releases.data && <span className="muted">Could not read the releases.</span>}
            {releases.data && (releases.data.items ?? []).length === 0 && <span className="muted">No release yet</span>}
            {(releases.data?.items ?? []).slice(0, 5).map((r) => {
              const where = pinnedWhere(r.releaseId);
              return (
                <div key={r.releaseId} className="home-status-item" data-testid={`home-release-${r.number}`}>
                  <span className="rh-num">#{r.number}</span>
                  <span>
                    from <code>{r.branchName}</code>
                  </span>
                  {r.createdAt && (
                    <span className="muted" title={formatInstantWithZone(r.createdAt, zone, { offsets })}>
                      built {relativeTime(r.createdAt, now)}
                    </span>
                  )}
                  {where.map((slug) => (
                    <Badge key={slug} tone="accent">
                      pinned on {slug}
                    </Badge>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The game home: Getting started, the releases, the status rows, branches, environments and the recent audit entries. */
export function GameHome() {
  const { org, game, environments, branches, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const audit = useQuery(`game:${org}/${game.slug}:audit`, () => client.data(ops.listGameAudit, { path: { org, game: game.slug }, query: { limit: 20 } }));
  const dirty = new Map(dirtyBranches(game.id).map((d) => [d.branch, d.count]));
  const onboarding = useOnboarding();
  const [hidden, setHidden] = useState(() => readOnboardingHidden(game.id));
  // the head link brings the card back even when every step is done
  const [forced, setForced] = useState(false);
  const showOnboarding = !hidden && (forced || !onboarding.allDone);
  const hideOnboarding = () => {
    writeOnboardingHidden(game.id, true);
    setHidden(true);
    setForced(false);
  };
  const showOnboardingAgain = () => {
    writeOnboardingHidden(game.id, false);
    setHidden(false);
    setForced(true);
  };
  const copyKey = () => {
    void navigator.clipboard?.writeText(game.publicKey ?? '').then(() => setCopied(true));
  };
  return (
    <section className="game-home">
      <div className="page-head">
        <h1>{game.name}</h1>
        {!showOnboarding && (
          <button type="button" className="link-button onboarding-show" onClick={showOnboardingAgain}>
            Getting started
          </button>
        )}
        <div className="facts">
          <span className="label-value">
            <span className="label">Slug</span>
            <code>{game.slug}</code>
          </span>
          <span className="label-value">
            <span className="label">Public key</span>
            <code>{game.publicKey}</code>
            <IconButton label="Copy the public key" onClick={copyKey}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            </IconButton>
          </span>
        </div>
      </div>

      {showOnboarding && <GettingStarted steps={onboarding.steps} onHide={hideOnboarding} />}

      <ReleasesSection />

      <StatusSection />

      <div className="section-head">
        <h2>Branches</h2>
        <span className="muted">The content lines of the game; the default one is what new work starts from.</span>
      </div>
      <DataGrid
        id="game-branches"
        rows={branches}
        rowKey={(b) => b.id}
        caption={`${branches.length} branch${branches.length === 1 ? '' : 'es'}`}
        height={320}
        onRowOpen={(b) => navigate(routes.templates(org, game.slug, b.name))}
        openOnClick
        columns={[
          { key: 'name', header: 'Branch', fixed: true, render: (b) => <Link className="mono" to={routes.templates(org, game.slug, b.name)}>{b.name}</Link>, cellText: (b) => b.name, sortValue: (b) => b.name },
          { key: 'desc', header: 'Description', render: (b) => b.description ?? '', cellText: (b) => b.description ?? '' },
          { key: 'flags', header: 'Flags', render: (b) => (
              <span className="row">
                {b.isDefault && <Badge tone="accent">default</Badge>}
                {b.isProtected && <Badge tone="info">protected</Badge>}
                {dirty.get(b.name) ? <Badge tone="warning">{dirty.get(b.name)} unsaved</Badge> : null}
              </span>
            ) },
          { key: 'head', header: 'Head', render: (b) => <code>{b.head?.slice(0, 10) ?? '—'}</code>, cellText: (b) => b.head ?? '' },
          { key: 'updated', header: 'Updated', render: (b) => formatInstantWithZone(b.updatedAt, zone, { offsets }), sortValue: (b) => b.updatedAt },
        ]}
      />

      <div className="page-section">
        <div className="section-head">
          <h2>Environments</h2>
          <span className="muted">Where releases are pinned and players live.</span>
        </div>
        <DataGrid
          id="game-envs"
          rows={environments}
          rowKey={(e) => e.id}
          height={240}
          columns={[
            { key: 'slug', header: 'Environment', fixed: true, render: (e) => <Link className="mono" to={routes.env(org, game.slug, e.slug)}>{e.slug}</Link> },
            { key: 'name', header: 'Name', render: (e) => e.name },
            { key: 'kind', header: 'Kind', render: (e) => <Badge tone={e.kind === 'prod' ? 'danger' : e.kind === 'stage' ? 'warning' : 'success'}>{e.kind}</Badge> },
            { key: 'time', header: 'Game time', render: (e) => (e.kind === 'prod' ? 'UTC' : e.useLocalTime ? 'device local time' : Number(e.gameTimeOffsetSeconds) ? `UTC ${Number(e.gameTimeOffsetSeconds) > 0 ? '+' : ''}${Number(e.gameTimeOffsetSeconds) / 3600} h` : 'UTC') },
            { key: 'retention', header: 'History', render: (e) => `${e.historyRetentionDays} d · ${e.historyMinVersionsPerKey} per key` },
          ]}
        />
      </div>

      <div className="page-section">
        <div className="section-head">
          <h2>Recent changes</h2>
          <span className="muted">The last 20 audit entries of the game.</span>
        </div>
        {audit.loading && !audit.data && <LoadingPane rows={3} label="Loading the recent changes" />}
        {audit.data && (
          <div className="pane">
            <div className="pane-body audit-log">
              <ChangeLog entries={audit.data.items} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
